import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Circle,
  GoogleMap,
  InfoWindow,
  Marker,
  OverlayView,
  Polyline,
  useJsApiLoader,
} from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from './mapProviders';
import { resolveAvatarUrl, getOnlineStatus } from '../../utils/locationUtils';
import EmployeeMarkerDetails from './EmployeeMarkerDetails';
import LocationName from './LocationName';
import './EmployeeMapGoogle.css';

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

const GOOGLE_MAP_TYPES = [
  { id: 'roadmap', label: 'Road' },
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'satellite', label: 'Satellite' },
  { id: 'terrain', label: 'Terrain' },
];

const STATUS_DOT_COLOR = {
  moving: '#3b82f6',
  'in-office': '#14b8a6',
  field: '#f59e0b',
  'checked-out': '#94a3b8',
  offline: '#fb7185',
  'no-location': '#cbd5e1',
};

const libraries = [];

const ProfileOverlay = ({ employee, isSelected, onClick }) => {
  const avatar = resolveAvatarUrl(employee.profilePic, employee.name);
  const online = getOnlineStatus(employee);
  const dotColor = online.isOnline
    ? '#22c55e'
    : STATUS_DOT_COLOR[employee.status] || STATUS_DOT_COLOR.offline;

  return (
    <OverlayView
      position={{ lat: employee.latitude, lng: employee.longitude }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(width, height) => ({
        x: -(width / 2),
        y: -height,
      })}
    >
      <button
        type="button"
        className={`google-map-marker ${isSelected ? 'is-selected' : ''} ${online.isOnline ? 'is-online' : ''} ${employee.isApproximateLocation ? 'is-approximate' : ''}`}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.(employee.userId);
        }}
        title={employee.name}
      >
        <img src={avatar} alt={employee.name} />
        <span className="google-map-marker-dot" style={{ background: dotColor }} />
        <span className="google-map-marker-tip">
          {employee.name} · {online.label}
          <br />
          <LocationName
            lat={employee.latitude}
            lng={employee.longitude}
            approximate={employee.isApproximateLocation}
            accuracy={employee.accuracy}
            compact
            showCoords={false}
          />
        </span>
      </button>
    </OverlayView>
  );
};

function EmployeeMapGoogle({
  employees = [],
  offices = [],
  selectedId,
  onSelectEmployee,
  center = { lat: 12.9716, lng: 80.2202 },
  zoom = 12,
  fullView = false,
  resizeToken = 0,
  searchPlace = null,
  fitRequestToken = 0,
}) {
  const mapRef = useRef(null);
  const userLockedRef = useRef(false);
  const initialFitDoneRef = useRef(false);
  const lastFitTokenRef = useRef(0);
  const lastSelectedIdRef = useRef(null);
  const [mapTypeId, setMapTypeId] = useState('hybrid');
  const [infoEmployee, setInfoEmployee] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'worklogz-google-maps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const locatedEmployees = useMemo(
    () =>
      employees.filter(
        (emp) => Number.isFinite(Number(emp.latitude)) && Number.isFinite(Number(emp.longitude))
      ),
    [employees]
  );

  const selectedEmployee = useMemo(
    () => locatedEmployees.find((emp) => emp.userId === selectedId) || null,
    [locatedEmployees, selectedId]
  );

  const trailPath = useMemo(() => {
    if (!selectedEmployee?.trail?.length) return [];
    return selectedEmployee.trail
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
      .map((p) => ({ lat: p.lat, lng: p.lng }));
  }, [selectedEmployee]);

  const fitToPoints = useCallback((map, points, maxZoom = 17) => {
    if (!map || !points.length) return;
    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(Math.max(map.getZoom() || 12, 18));
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 70);
    const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
    });
    return () => window.google.maps.event.removeListener(listener);
  }, []);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    map.addListener('dragstart', () => {
      userLockedRef.current = true;
    });
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof map.setOptions !== 'function') return;
    // Trigger resize after full-view / sidebar changes
    window.google?.maps?.event?.trigger(map, 'resize');
  }, [resizeToken, fullView]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !searchPlace?.lat || !searchPlace?.lng) return;
    userLockedRef.current = true;
    map.panTo({ lat: searchPlace.lat, lng: searchPlace.lng });
    if ((map.getZoom() || 0) < 15) map.setZoom(15);
  }, [searchPlace?.id, searchPlace?.lat, searchPlace?.lng]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const forceFit = fitRequestToken > 0 && fitRequestToken !== lastFitTokenRef.current;
    if (forceFit) {
      lastFitTokenRef.current = fitRequestToken;
      userLockedRef.current = false;
      initialFitDoneRef.current = false;
    }

    if (initialFitDoneRef.current || (userLockedRef.current && !forceFit) || (selectedId && !forceFit)) {
      return;
    }

    const points = [
      ...locatedEmployees.map((e) => ({ lat: Number(e.latitude), lng: Number(e.longitude) })),
      ...offices.map((o) => ({ lat: Number(o.latitude), lng: Number(o.longitude) })),
    ].filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));

    if (!points.length) return;
    initialFitDoneRef.current = true;
    fitToPoints(map, points);
  }, [locatedEmployees, offices, selectedId, fitRequestToken, fitToPoints]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedEmployee) {
      lastSelectedIdRef.current = null;
      return;
    }

    const isNew = lastSelectedIdRef.current !== selectedEmployee.userId;
    lastSelectedIdRef.current = selectedEmployee.userId;
    if (!isNew) return;

    userLockedRef.current = true;
    map.panTo({
      lat: Number(selectedEmployee.latitude),
      lng: Number(selectedEmployee.longitude),
    });
    if ((map.getZoom() || 0) < 18) map.setZoom(18);
    setInfoEmployee(selectedEmployee);
  }, [selectedEmployee]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className={`google-map-shell ${fullView ? 'is-fullview' : ''}`}>
        <div className="google-map-missing-key">
          <p className="font-bold text-[#1a365d]">Google Maps API key required</p>
          <p className="text-sm text-slate-600 mt-1">
            Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to <code>client/.env</code>, enable
            Maps JavaScript API in Google Cloud, then restart the client.
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`google-map-shell ${fullView ? 'is-fullview' : ''}`}>
        <div className="google-map-missing-key">
          <p className="font-bold text-rose-700">Google Maps failed to load</p>
          <p className="text-sm text-slate-600 mt-1">
            Check the API key, billing, and that Maps JavaScript API is enabled for this key.
          </p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={`google-map-shell ${fullView ? 'is-fullview' : ''}`}>
        <div className="google-map-loading">Loading Google Maps…</div>
      </div>
    );
  }

  return (
    <div className={`google-map-shell ${fullView ? 'is-fullview' : ''}`}>
      <div className="google-map-type-toggle">
        {GOOGLE_MAP_TYPES.map((type) => (
          <button
            key={type.id}
            type="button"
            className={mapTypeId === type.id ? 'is-active' : ''}
            onClick={() => setMapTypeId(type.id)}
          >
            {type.label}
          </button>
        ))}
      </div>

      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        mapContainerClassName="google-map-canvas"
        center={center}
        zoom={zoom}
        onLoad={onMapLoad}
        onClick={() => setInfoEmployee(null)}
        options={{
          mapTypeId,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
          minZoom: 3,
          maxZoom: 21,
        }}
      >
        {offices.map((office) => (
          <React.Fragment key={office.name}>
            <Marker
              position={{ lat: Number(office.latitude), lng: Number(office.longitude) }}
              title={office.name}
              label={{
                text: office.name.slice(0, 1).toUpperCase(),
                color: '#fff',
                fontWeight: '700',
              }}
            />
            <Circle
              center={{ lat: Number(office.latitude), lng: Number(office.longitude) }}
              radius={office.radiusMeters || 400}
              options={{
                strokeColor: '#0d9488',
                strokeOpacity: 0.8,
                strokeWeight: 1,
                fillColor: '#14b8a6',
                fillOpacity: 0.08,
              }}
            />
          </React.Fragment>
        ))}

        {trailPath.length > 1 ? (
          <Polyline
            path={trailPath}
            options={{
              strokeColor: '#1a365d',
              strokeOpacity: 0.85,
              strokeWeight: 4,
            }}
          />
        ) : null}

        {locatedEmployees.map((employee) => (
          <ProfileOverlay
            key={employee.userId}
            employee={employee}
            isSelected={employee.userId === selectedId}
            onClick={(id) => {
              const next = id === selectedId ? null : id;
              onSelectEmployee?.(next);
              setInfoEmployee(next ? employee : null);
            }}
          />
        ))}

        {searchPlace?.lat != null && searchPlace?.lng != null ? (
          <Marker
            position={{ lat: searchPlace.lat, lng: searchPlace.lng }}
            title={searchPlace.shortLabel || searchPlace.label}
          />
        ) : null}

        {infoEmployee ? (
          <InfoWindow
            position={{
              lat: Number(infoEmployee.latitude),
              lng: Number(infoEmployee.longitude),
            }}
            onCloseClick={() => setInfoEmployee(null)}
          >
            <EmployeeMarkerDetails employee={infoEmployee} />
          </InfoWindow>
        ) : null}
      </GoogleMap>
    </div>
  );
}

export default EmployeeMapGoogle;
